import { useMediaQuery } from "@/hooks/use-media-query";
import React, { useEffect, useState, startTransition } from "react";
import { Dialog, DialogClose, DialogContent } from "../ui/dialog";
import { Carousel, CarouselApi, CarouselNext, CarouselPrevious } from "../ui/carousel";
import { CarouselContent } from "../ui/carousel";
import { CarouselItem } from "../ui/carousel";
import Image from "next/image";
import { cn, formatPrice } from "@/lib/utils";
import {
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  InformationCircleIcon,
  WrenchIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { CarouselDots } from "../ui/carousel";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { FieldLabel } from "../ui/field";
import { Button } from "../ui/button";
import { Product } from "@/lib/types/product";
import { Drawer, DrawerContent } from "../ui/drawer";
import AddToCartDialogSkeletonLoader from "../atoms/skeleton-loaders/add-to-cart-dialog-skeleton-loader";
import { useCart } from "@/hooks/useCart";
import { toast } from "sonner";
import { QuantityCounter } from "../ui/quantity-counter";
import { StarIcon as StarOutline } from "@heroicons/react/24/outline";
import { StarIcon as StarSolid } from "@heroicons/react/24/solid";
import { ShieldCheckIcon as ShieldCheckIconOutline } from "@heroicons/react/24/outline";
import ProductTabs from "@/app/(user-marketplace)/(global)/(marketplace)/shop/[id]/_components/product-tabs";
import ShoppingCartIcon from "../atoms/dynamic-icons/shopping-cart";

export default function AddToCartDialog({
  open,
  setOpen,
  id,
}: {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  id: number;
}) {
  const isDesktop = useMediaQuery("(min-width: 768px)");

  const product = {
    id: id,
    name: "Product 1",
    price: 100000,
    noOfPurchases: 123000,
    category: "Category 1",
    subCategory: "Sub Category 1",
    brand: "Brand 1",
    vendor: "Vendor 1",
    model: "Model 1",
    color: "Color 1",
    material: "Material 1",
    size: "Size 1",
    images: [
      "https://plus.unsplash.com/premium_photo-1683917067988-1b0323c3b574?q=80&w=1974&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
      "https://images.unsplash.com/photo-1615874959474-d609969a20ed?q=80&w=2080&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
      "https://images.unsplash.com/photo-1611892440504-42a792e24d32?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8OHx8YmVkcm9vbSUyMGludGVyaW9yfGVufDB8fDB8fHww",
      "https://images.unsplash.com/photo-1633948393301-d43e3ec0e5cd?q=80&w=1171&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
    ],
    description:
      "Product 1 description Lorem ipsum dolor sit amet consectetur adipisicing elit. Quisquam, quos. Lorem ipsum dolor sit amet consectetur adipisicing elit. Quisquam, quos.",
    rating: 4.5,
    noOfReviews: 100,
    reviewsCount: 100,
    installationSupport: true,
    assetProtection: true,
  };

  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const isLoading = false;

  console.log(count);

  useEffect(() => {
    if (!api) {
      return;
    }

    startTransition(() => {
      setCount(api.scrollSnapList().length);
      setCurrent(api.selectedScrollSnap() + 1);
    });

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap() + 1);
    });
  }, [api]);
  const goToSlide = (index: number) => {
    api?.scrollTo(index);
  };

  if (isDesktop) {
    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          showCloseButton={false}
          className='scrollbar-hide max-h-[calc(100vh-4rem)] overflow-x-hidden overflow-y-auto pt-14 sm:max-w-5xl'
        >
          <DialogClose asChild>
            <Button variant='ghost' className='absolute top-4 right-4' title='Close'>
              <XMarkIcon className='size-5' />
            </Button>
          </DialogClose>
          {isLoading ? (
            <AddToCartDialogSkeletonLoader />
          ) : (
            <CatalogueAddToCartForm
              setApi={setApi}
              product={product}
              current={current}
              setCurrent={setCurrent}
              quantity={quantity}
              setQuantity={setQuantity}
              goToSlide={goToSlide}
            />
          )}
        </DialogContent>
      </Dialog>
    );
  }
  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerContent>
        <div className='h-full w-full overflow-y-auto'>
          {isLoading ? (
            <AddToCartDialogSkeletonLoader />
          ) : (
            <CatalogueAddToCartForm
              setApi={setApi}
              product={product}
              current={current}
              setCurrent={setCurrent}
              quantity={quantity}
              setQuantity={setQuantity}
              goToSlide={goToSlide}
            />
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}

function CatalogueAddToCartForm({
  setApi,
  product,
  current,
  quantity,
  setQuantity,
  goToSlide,
}: {
  setApi: (api: CarouselApi) => void;
  product: Product;
  current: number;
  setCurrent?: (current: number) => void;
  quantity: number;
  setQuantity: React.Dispatch<React.SetStateAction<number>>;
  goToSlide: (index: number) => void;
}) {
  const { addToCart, isAdding } = useCart();
  const [selectedStyle, setSelectedStyle] = useState<string>("");
  const [selectedSize, setSelectedSize] = useState<string>("");
  const [selectedColor, setSelectedColor] = useState<string>("");

  // todo: Mock options - in a real app, these would come from the product data
  const styleOptions = ["Modern", "Classic", "Minimalist", "Vintage"];
  const sizeOptions = ["Small", "Medium", "Large", "Extra Large"];
  const colorOptions = ["Black", "White", "Gray", "Brown"];

  const handleAddToCart = () => {
    if (!selectedStyle) {
      toast.error("Please select a style");
      return;
    }
    if (!selectedSize) {
      toast.error("Please select a size");
      return;
    }
    if (!selectedColor) {
      toast.error("Please select a color");
      return;
    }

    addToCart({
      productId: product.id,
      name: product.name,
      price: product.price,
      quantity: quantity,
      image: product.images?.[0] || "",
      tags: [selectedStyle, selectedSize, selectedColor],
      variants: {
        style: selectedStyle,
        size: selectedSize,
        color: selectedColor,
      },
      stock: 50,
    });
  };

  return (
    <div className='flex w-full flex-col px-2 md:flex-row md:gap-8 md:px-0'>
      <aside className='w-full shrink-0 md:w-[50%]'>
        <div className='md:sticky md:top-2'>
          <div className='flex items-start gap-2'>
            {/* Thumbnail Navigation */}
            <div className='hidden gap-2 overflow-x-hidden overflow-y-auto px-5 py-3 md:flex md:flex-col'>
              {product.images.map((image, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  title={`View image ${index + 1}`}
                  className={cn(
                    "relative h-20 w-16 shrink-0 overflow-hidden rounded-lg transition-all duration-200 hover:scale-105",
                    current === index + 1
                      ? "ring-link-primary ring-2 ring-offset-2"
                      : "ring-1 ring-gray-200 hover:ring-gray-300"
                  )}
                >
                  <Image
                    src={image}
                    alt={`${product.name} - Thumbnail ${index + 1}`}
                    width={44}
                    height={44}
                    className='h-full w-full object-cover'
                  />
                  {current === index + 1 && <div className='absolute inset-0 bg-black/10' />}
                </button>
              ))}
            </div>
            <Carousel className='relative w-full overflow-hidden rounded-lg' setApi={setApi}>
              <CarouselContent className='-ml-5 rounded-lg'>
                {product.images.map((image, index) => (
                  <CarouselItem className='' key={index}>
                    <div className='bg-muted relative aspect-square overflow-hidden rounded-lg'>
                      <Image
                        src={image}
                        alt={`${product.name} - Image ${index + 1}`}
                        width={400}
                        height={400}
                        className='h-full w-full rounded-lg object-contain'
                      />
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselDots className='md:hidden' />
              <CarouselPrevious
                variant='ghost'
                className='disabled:bg-bg-base bg-bg-base absolute top-1/2 left-4 z-10 -translate-y-1/2 disabled:opacity-60'
                customIcon={<ChevronLeftIcon className='size-4' />}
                title='Previous image'
              />
              <CarouselNext
                variant='ghost'
                className='disabled:bg-bg-base bg-bg-base absolute top-1/2 right-4 z-10 -translate-y-1/2 disabled:opacity-60'
                customIcon={<ChevronRightIcon className='size-4' />}
                title='Next image'
              />
            </Carousel>
          </div>
          <div className='mt-8 hidden md:block'>
            <ProductTabs description={product.description} noOfReviews={product.noOfReviews} />
          </div>
        </div>
      </aside>
      <main className='flex-1'>
        <div className='divide-border-title/10 h-fit divide-y-[1.5px] md:sticky md:top-2'>
          <div className='space-y-1 py-4 md:space-y-2 md:pb-4'>
            <h3 className='text-text-title text-lg font-light md:text-xl'>{product.name}</h3>
            <p className='text-text-title/70 hidden text-xs leading-[160%] font-normal md:block'>
              {product.description}
            </p>
            <div className='divide-border-title/10 flex items-center space-x-2 divide-x'>
              <p className='flex items-center gap-1 pr-2'>
                {Array.from({ length: 5 }).map((_, index) => {
                  const fullStars = Math.floor(product.rating);
                  const hasHalf = product.rating - fullStars >= 0.5;
                  const isFull = index < fullStars;
                  const isHalf = !isFull && index === fullStars && hasHalf;
                  return (
                    <span key={index} className='relative inline-flex'>
                      {/* Background fill star (behind outline) */}
                      <StarSolid
                        className={
                          isFull
                            ? "size-4.5 text-[#FFB900]"
                            : isHalf
                              ? "size-4.5 text-[#FFB900]/50"
                              : "size-4.5 text-transparent"
                        }
                      />
                      {/* Outline star on top */}
                      <StarOutline className='absolute inset-0 size-4.5 text-[#FFB900]' />
                    </span>
                  );
                })}

                <span className='text-text-title/70 pl-2 text-xs font-normal'>({product.rating})</span>
              </p>
              {product.noOfReviews > 0 && <p>{product.noOfReviews} reviews</p>}
            </div>
            {product.installationSupport && (
              <div className='border-border-title/10 my-4 rounded-lg border'>
                <Accordion type='single' collapsible>
                  <AccordionItem value='installation' className='border-none'>
                    <AccordionTrigger className='border-border-title/10 cursor-pointer bg-[#F7F7F7] px-4 hover:no-underline data-[state=open]:rounded-b-none data-[state=open]:border-b dark:bg-transparent [&>svg]:hidden'>
                      <div className='flex w-full items-center gap-3'>
                        <div className='flex size-8 shrink-0 items-center justify-center rounded-full bg-[#ECECEC]'>
                          <WrenchIcon className='size-4 text-[#364153]' />
                        </div>
                        <div className='flex-1 text-left'>
                          <h4 className='text-text-title text-sm font-semibold'>Installation</h4>
                          <p className='text-text-title/70 mt-0.5 text-xs font-normal'>
                            Ensure safe and optimal setup for {formatPrice(8500)}
                          </p>
                        </div>
                        <div className='flex size-6 shrink-0 items-center justify-center rounded-full bg-gray-100 dark:bg-transparent'>
                          <InformationCircleIcon className='size-4 text-[#CDCDCD]' />
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className='p-4'>
                      <div className='space-y-4'>
                        <div>
                          <h5 className='text-text-title mb-2 text-sm font-semibold'>What is</h5>
                          <p className='text-text-title/70 text-sm leading-relaxed'>
                            Our certified technicians will expertly install your new solar panels and/or inverter system
                            for peak performance and safety. This includes:
                          </p>
                        </div>
                        <ul className='text-text-title/70 space-y-2 text-sm leading-relaxed'>
                          <li className='flex items-start gap-2'>
                            <span className='mt-1.5 size-1.5 shrink-0 rounded-full bg-[#364153]' />
                            <span>Professional mounting and wiring of all equipment (panels, inverter, batteries)</span>
                          </li>
                          <li className='flex items-start gap-2'>
                            <span className='mt-1.5 size-1.5 shrink-0 rounded-full bg-[#364153]' />
                            <span>System testing and commissioning to ensure full functionality</span>
                          </li>
                          <li className='flex items-start gap-2'>
                            <span className='mt-1.5 size-1.5 shrink-0 rounded-full bg-[#364153]' />
                            <span>Guidance and handover pack with manuals and maintenance advice</span>
                          </li>
                          <li className='flex items-start gap-2'>
                            <span className='mt-1.5 size-1.5 shrink-0 rounded-full bg-[#364153]' />
                            <span>Comprehensive site assessment and system design verification</span>
                          </li>
                          <li className='flex items-start gap-2'>
                            <span className='mt-1.5 size-1.5 shrink-0 rounded-full bg-[#364153]' />
                            <span>2-year warranty on the installation work</span>
                          </li>
                        </ul>
                        <div className='border-t border-gray-200 pt-4'>
                          <p className='text-text-title mb-4 text-sm font-semibold'>
                            Would you like to add the Professional Installation Service to your order?
                          </p>
                          {/* todo: add add installation behaviour here */}
                          <div className='flex flex-col gap-3 md:flex-row'>
                            <Button size='sm' className='flex-1 rounded-xs py-2 md:py-0'>
                              <CheckIcon className='size-4' />
                              Yes, Add Installation ({formatPrice(8500)})
                            </Button>
                            <div className='flex-1'>
                              <AccordionTrigger className='py-0 hover:no-underline [&>svg]:hidden'>
                                <Button variant='outline' size='sm' className='w-full rounded-xs'>
                                  <XMarkIcon className='size-4' />
                                  No, Thanks
                                </Button>
                              </AccordionTrigger>
                            </div>
                          </div>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            )}
            {product.assetProtection && (
              <div className='border-border-title/10 my-4 rounded-lg border'>
                <Accordion type='single' collapsible>
                  <AccordionItem value='installation' className='border-none'>
                    <AccordionTrigger className='border-border-title/10 cursor-pointer bg-[#F7F7F7] px-4 hover:no-underline data-[state=open]:rounded-b-none data-[state=open]:border-b dark:bg-transparent [&>svg]:hidden'>
                      <div className='flex w-full items-center gap-3'>
                        <div className='flex size-8 shrink-0 items-center justify-center rounded-full bg-[#ECECEC]'>
                          <ShieldCheckIconOutline className='size-4 text-[#364153]' />
                        </div>
                        <div className='flex-1 text-left'>
                          <h4 className='text-text-title text-sm font-semibold'>Asset Protection</h4>
                          <p className='text-text-title/70 mt-0.5 text-xs font-normal'>
                            Protect your purchase for {formatPrice(85000)}
                          </p>
                        </div>
                        <div className='flex size-6 shrink-0 items-center justify-center rounded-full bg-gray-100 dark:bg-transparent'>
                          <InformationCircleIcon className='size-4 text-[#CDCDCD]' />
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className='p-4'>
                      <div className='space-y-4'>
                        <div>
                          <h5 className='text-text-title mb-2 text-sm font-semibold'>What is</h5>
                          <p className='text-text-title/70 text-sm leading-relaxed'>
                            Our certified technicians will expertly install your new solar panels and/or inverter system
                            for peak performance and safety. This includes:
                          </p>
                        </div>
                        <ul className='text-text-title/70 space-y-2 text-sm leading-relaxed'>
                          <li className='flex items-start gap-2'>
                            <span className='mt-1.5 size-1.5 shrink-0 rounded-full bg-[#364153]' />
                            <span>Professional mounting and wiring of all equipment (panels, inverter, batteries)</span>
                          </li>
                          <li className='flex items-start gap-2'>
                            <span className='mt-1.5 size-1.5 shrink-0 rounded-full bg-[#364153]' />
                            <span>System testing and commissioning to ensure full functionality</span>
                          </li>
                          <li className='flex items-start gap-2'>
                            <span className='mt-1.5 size-1.5 shrink-0 rounded-full bg-[#364153]' />
                            <span>Guidance and handover pack with manuals and maintenance advice</span>
                          </li>
                          <li className='flex items-start gap-2'>
                            <span className='mt-1.5 size-1.5 shrink-0 rounded-full bg-[#364153]' />
                            <span>Comprehensive site assessment and system design verification</span>
                          </li>
                          <li className='flex items-start gap-2'>
                            <span className='mt-1.5 size-1.5 shrink-0 rounded-full bg-[#364153]' />
                            <span>2-year warranty on the installation work</span>
                          </li>
                        </ul>
                        <div className='border-t border-gray-200 pt-4'>
                          <p className='text-text-title mb-4 text-sm font-semibold'>
                            Would you like to add the Professional Installation Service to your order?
                          </p>
                          {/* todo: add add installation behaviour here */}
                          <div className='flex flex-col gap-3 md:flex-row'>
                            <Button size='sm' className='flex-1 rounded-xs py-2 md:py-0'>
                              <CheckIcon className='size-4' />
                              Yes, Add Installation ({formatPrice(8500)})
                            </Button>
                            <div className='flex-1'>
                              <AccordionTrigger className='py-0 hover:no-underline [&>svg]:hidden'>
                                <Button variant='outline' size='sm' className='w-full rounded-xs'>
                                  <XMarkIcon className='size-4' />
                                  No, Thanks
                                </Button>
                              </AccordionTrigger>
                            </div>
                          </div>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            )}
            <p className='text-text-title text-lg font-semibold select-none md:text-xl'>{formatPrice(product.price)}</p>
          </div>
          <div className='pb-4'>
            <Accordion type='single' collapsible>
              {/* Style Accordion */}
              <AccordionItem value='item-1'>
                <AccordionTrigger className='hover:no-underline'>
                  <h4 className='text-sm select-none'>
                    Style:{" "}
                    {selectedStyle && <span className='text-text-title/60 ml-1 font-normal'>{selectedStyle}</span>}
                  </h4>
                </AccordionTrigger>
                <AccordionContent>
                  <RadioGroup
                    value={selectedStyle}
                    onValueChange={setSelectedStyle}
                    className='flex w-full flex-wrap px-1 py-1'
                  >
                    {styleOptions.map((style) => (
                      <FieldLabel
                        key={style}
                        htmlFor={`style-${style}`}
                        className='has-data-[state=checked]:ring-border-title border-border-primmary text-text-title/60 cursor-pointer rounded-full border px-4 py-2 font-normal transition-all has-data-[state=checked]:bg-transparent has-data-[state=checked]:ring-1 md:px-4 md:py-2'
                      >
                        {style}
                        <RadioGroupItem id={`style-${style}`} value={style} className='hidden' />
                      </FieldLabel>
                    ))}
                  </RadioGroup>
                </AccordionContent>
              </AccordionItem>

              {/* Size Accordion */}
              <AccordionItem value='item-2'>
                <AccordionTrigger className='hover:no-underline'>
                  <h4 className='text-sm select-none'>
                    Size: {selectedSize && <span className='text-text-title/60 ml-1 font-normal'>{selectedSize}</span>}
                  </h4>
                </AccordionTrigger>
                <AccordionContent>
                  <RadioGroup
                    value={selectedSize}
                    onValueChange={setSelectedSize}
                    className='flex w-full flex-wrap px-1 py-1'
                  >
                    {sizeOptions.map((size) => (
                      <FieldLabel
                        key={size}
                        htmlFor={`size-${size}`}
                        className='has-data-[state=checked]:ring-border-title border-border-primmary text-text-title/60 cursor-pointer rounded-full border px-4 py-2 font-normal transition-all has-data-[state=checked]:bg-transparent has-data-[state=checked]:ring-1 md:px-4 md:py-2'
                      >
                        {size}
                        <RadioGroupItem id={`size-${size}`} value={size} className='hidden' />
                      </FieldLabel>
                    ))}
                  </RadioGroup>
                </AccordionContent>
              </AccordionItem>

              {/* Color Accordion */}
              <AccordionItem value='item-3'>
                <AccordionTrigger className='hover:no-underline'>
                  <h4 className='text-sm select-none'>
                    Color:{" "}
                    {selectedColor && <span className='text-text-title/60 ml-1 font-normal'>{selectedColor}</span>}
                  </h4>
                </AccordionTrigger>
                <AccordionContent>
                  <RadioGroup
                    value={selectedColor}
                    onValueChange={setSelectedColor}
                    className='flex w-full flex-wrap px-1 py-1'
                  >
                    {colorOptions.map((color) => (
                      <FieldLabel
                        key={color}
                        htmlFor={`color-${color}`}
                        className='has-data-[state=checked]:ring-border-title border-border-primmary text-text-title/60 cursor-pointer rounded-full border px-4 py-2 font-normal transition-all has-data-[state=checked]:bg-transparent has-data-[state=checked]:ring-1 md:px-4 md:py-2'
                      >
                        {color}
                        <RadioGroupItem id={`color-${color}`} value={color} className='hidden' />
                      </FieldLabel>
                    ))}
                  </RadioGroup>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
            <div className='flex h-10 items-center gap-4'>
              <QuantityCounter value={quantity} onChange={setQuantity} min={1} containerClassName='h-full' />
              <Button
                onClick={handleAddToCart}
                disabled={isAdding || !selectedStyle || !selectedSize || !selectedColor}
                title={`${
                  !selectedStyle
                    ? "Please select a style"
                    : !selectedSize
                      ? "Please select a size"
                      : !selectedColor
                        ? "Please select a color"
                        : isAdding
                          ? "Adding..."
                          : "Add to your Cart"
                }`}
                className='h-full flex-1 text-xs font-medium tracking-[1.4px] uppercase md:h-full md:text-sm'
              >
                <ShoppingCartIcon className='size-4' />
                {isAdding ? "Adding..." : "Add to your Cart"}
              </Button>
            </div>
          </div>
        </div>
        <div className='mt-8 block md:hidden'>
          <ProductTabs description={product.description} noOfReviews={product.noOfReviews} />
        </div>
      </main>
    </div>
  );
}
